'use client';

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    BarChart3,
    Users,
    ArrowRight,
    RefreshCw,
    X,
    Activity,
    Target,
    ListTree,
    Split,
    ChevronDown,
    AlertTriangle,
    TrendingDown,
    Maximize2,
    Minimize2,
    Workflow,
    AlignStartVertical,
} from 'lucide-react';
import { endOfDay, startOfDay, subDays } from 'date-fns';

import { AnalyticsErrorBoundary } from './AnalyticsErrorBoundary';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import * as SelectPrimitive from '@radix-ui/react-select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/api/trpc';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

import { FunnelAnalyticsFilters, type DateRange, type Granularity } from './FunnelAnalyticsFilters';
import { clampAnalyticsRange } from './analyticsRange';
import { StepProgressionChart } from './StepProgressionChart';
import { SessionTracesPanel } from './SessionTracesPanel';
import { FunnelBreakdownPanel, type BreakdownScope } from './FunnelBreakdownPanel';
import { BranchBreakdown, FunnelSankey, type FunnelFlow } from './FunnelBranching';

interface FunnelStepVariant {
    id: string;
    label: string;
}

interface FunnelStep {
    id: string;
    name: string;
    type: string;
    variants?: FunnelStepVariant[];
}

interface FunnelEdge {
    id: string;
    source: string;
    target: string;
}

interface FunnelAnalyticsPanelProps {
    funnelId: string;
    storeId: string;
    steps: FunnelStep[];
    edges?: FunnelEdge[];
    onClose?: () => void;
    className?: string;
    /** Whether the funnel has a promoted production config. Published funnels
     *  default the analytics environment to Live. */
    isPublished?: boolean;
    selectedStepId?: string | null;
    onStepSelect?: (stepId: string | null) => void;
    dateRange?: DateRange;
    onDateRangeChange?: (range: DateRange) => void;
    /** Whether the panel currently fills the whole editor (desktop). */
    isFullscreen?: boolean;
    /** Toggle between the side-by-side split and the full-screen view. */
    onToggleFullscreen?: () => void;
}

/**
 * Comprehensive Funnel Analytics Panel
 * 
 * Features:
 * - Step-to-step progression analytics over time
 * - Date range filtering with presets
 * - Granularity control (daily/weekly/monthly)
 * - Session and visitor insights
 */
export const FunnelAnalyticsPanel = React.memo(function FunnelAnalyticsPanel({
    funnelId,
    storeId,
    steps,
    edges = [],
    onClose,
    className,
    isPublished = false,
    selectedStepId,
    onStepSelect,
    dateRange: externalDateRange,
    onDateRangeChange: externalOnDateRangeChange,
    isFullscreen = false,
    onToggleFullscreen,
}: FunnelAnalyticsPanelProps) {
    const { t } = useTranslation('storefront');

    const [activeTab, setActiveTab] = useState<'analytics' | 'sessions'>('analytics');

    // Environment scope for every analytics query in the panel. Decoupled from
    // the editor's staging/live toggle: published funnels default to Live
    // (production traffic), unpublished ones only have editor/preview traffic.
    const [environment, setEnvironment] = useState<'staging' | 'production'>(
        isPublished ? 'production' : 'staging',
    );
    const envTouchedRef = useRef(false);
    useEffect(() => {
        // Funnel data can arrive after mount — snap the default to Live once
        // publication state is known, unless the user already picked one.
        if (!envTouchedRef.current) {
            setEnvironment(isPublished ? 'production' : 'staging');
        }
    }, [isPublished]);

    // External storefront steps (WooCommerce/Shopify) run on the merchant's own
    // site and only ever emit production-tagged events — in Editor view they can
    // never have data, so exclude them instead of rendering a permanently-zero
    // entry step that drags the headline metrics to 0.
    const safeSteps = useMemo(() => {
        const list = Array.isArray(steps) ? steps : [];
        if (environment === 'staging') {
            return list.filter(
                (s) =>
                    s.type !== 'storefront_woocommerce' &&
                    s.type !== 'storefront_shopify' &&
                    s.type !== 'external',
            );
        }
        return list;
    }, [steps, environment]);
    const hasSteps = safeSteps.length > 0;

    const [internalDateRange, setInternalDateRange] = useState<DateRange>(() => ({
        from: startOfDay(subDays(new Date(), 7)),
        to: endOfDay(new Date()),
    }));
    const dateRange = externalDateRange ?? internalDateRange;
    const setDateRange = externalOnDateRangeChange ?? setInternalDateRange;

    // `dateRange` keeps the raw UI range (incl. the epoch→far-future "All time"
    // sentinel) for labels/calendar, but analytics queries must respect the
    // server's max-span cap — clamp once here and feed every query the bounded
    // range, otherwise "All time" 400s with "Date range too large".
    const queryRange = useMemo(
        () => clampAnalyticsRange(dateRange.from, dateRange.to),
        [dateRange.from, dateRange.to],
    );

    const [granularity, setGranularity] = useState<Granularity>('daily');

    // Build edge-based adjacency: stepId -> first target from edges (follows the actual funnel graph)
    const edgeTargetMap = useMemo(() => {
        const map: Record<string, string> = {};
        for (const edge of edges) {
            if (!map[edge.source]) {
                map[edge.source] = edge.target;
            }
        }
        return map;
    }, [edges]);

    // Step selection for progression chart - use edges to pick the correct next step
    const firstStepId = safeSteps[0]?.id || '';
    const [sourceStepId, setSourceStepId] = useState<string>(firstStepId);
    const [targetStepId, setTargetStepId] = useState<string>(
        edgeTargetMap[firstStepId] || safeSteps[1]?.id || ''
    );

    // Helper: find next step for a given stepId using edges or array fallback
    const findNextStepId = useCallback((stepId: string, stepsRef: FunnelStep[]): string | null => {
        const edgeTarget = edgeTargetMap[stepId];
        if (edgeTarget && edgeTarget !== stepId) return edgeTarget;
        const idx = stepsRef.findIndex(s => s.id === stepId);
        if (idx >= 0 && idx < stepsRef.length - 1) {
            return stepsRef[idx + 1].id;
        }
        return null;
    }, [edgeTargetMap]);

    const hasManuallySelectedRef = useRef(false);

    // Keep the progression selection valid when the visible step set changes
    // (switching to Editor view hides external storefront steps).
    useEffect(() => {
        if (!hasSteps) return;
        const sourceValid = safeSteps.some((s) => s.id === sourceStepId);
        const targetValid = safeSteps.some((s) => s.id === targetStepId);
        if (!sourceValid) {
            const fallback = safeSteps[0].id;
            setSourceStepId(fallback);
            setTargetStepId(findNextStepId(fallback, safeSteps) || '');
        } else if (!targetValid) {
            setTargetStepId(findNextStepId(sourceStepId, safeSteps) || '');
        }
    }, [safeSteps, hasSteps, sourceStepId, targetStepId, findNextStepId]);

    // Helper to check if a step has outgoing edges (is not an end step)
    const hasOutgoingEdges = useMemo(() => {
        const stepsWithOutgoing = new Set<string>();
        for (const edge of edges) {
            stepsWithOutgoing.add(edge.source);
        }
        return (stepId: string) => stepsWithOutgoing.has(stepId);
    }, [edges]);

    // Find step names
    const sourceStep = safeSteps.find(s => s.id === sourceStepId);
    const targetStep = safeSteps.find(s => s.id === targetStepId);

    // Determine if the selected ID is a variant (for filtering)
    const selectedVariantInfo = useMemo(() => {
        if (!selectedStepId) return null;

        for (const step of safeSteps) {
            if (step.variants) {
                const variant = step.variants.find(v => v.id === selectedStepId);
                if (variant) {
                    return {
                        variantId: variant.id,
                        stepId: step.id,
                        label: variant.label,
                    };
                }
            }
        }
        return null;
    }, [selectedStepId, safeSteps]);

    // Get the actual step ID (either direct step or parent step of variant)
    const effectiveStepId = selectedVariantInfo?.stepId || selectedStepId;

    // Debug: Check if TRPC procedures exist
    if (process.env.NODE_ENV === 'development') {
        if (!trpc.analytics?.stepToStepProgression) {
            console.error('[FunnelAnalyticsPanel] CRITICAL: trpc.analytics.stepToStepProgression is missing!');
        }
        if (!trpc.analytics?.funnelAnalytics) {
            console.error('[FunnelAnalyticsPanel] CRITICAL: trpc.analytics.funnelAnalytics is missing!');
        }
        if (!trpc.analytics?.funnelBreakdown) {
            console.error('[FunnelAnalyticsPanel] CRITICAL: trpc.analytics.funnelBreakdown is missing!');
        }
    }

    // Step progression query
    // Note: Type assertion needed until TRPC types are regenerated (backend supports splitByVariant/variantId)
    // Safe access to useQuery to prevent crash if procedure is missing
    const useStepProgressionQuery = trpc.analytics?.stepToStepProgression?.useQuery;

    const {
        data: rawProgressionData,
        isLoading: isLoadingProgression,
        refetch: refetchProgression,
        error: progressionError,
    } = useStepProgressionQuery ? useStepProgressionQuery(
        {
            funnelId,
            sourceStepId,
            targetStepId,
            granularity,
            startDate: queryRange.from,
            endDate: queryRange.to,
            storeIds: [storeId],
            environment,
            splitByVariant: true, // Show per-variant curves for A/B tests
            variantId: selectedVariantInfo?.variantId, // Filter to specific variant if selected
        } as Parameters<typeof trpc.analytics.stepToStepProgression.useQuery>[0],
        {
            enabled: !!sourceStepId && !!targetStepId && sourceStepId !== targetStepId && hasSteps,
            staleTime: 30000,
        }
    ) : { data: undefined, isLoading: false, refetch: () => { }, error: null };

    // Log any query errors in development
    if (progressionError && process.env.NODE_ENV === 'development') {
        console.error('[FunnelAnalyticsPanel] Progression query error:', progressionError);
    }

    // Extended type for progression data with variant support (until TRPC types regenerated)
    interface ExtendedProgressionData {
        dataPoints: any[];
        summary: any;
        isSplitTest?: boolean;
        variants?: Array<{ variantId: string; variantLabel: string | null }>;
        variantDataPoints?: Record<string, any[]>;
        variantSummaries?: Record<string, any>;
    }
    const progressionData = rawProgressionData as ExtendedProgressionData | undefined;

    // Funnel overall analytics
    const useFunnelAnalyticsQuery = trpc.analytics?.funnelAnalytics?.useQuery;

    const {
        data: funnelAnalytics,
        isLoading: isLoadingFunnel,
        refetch: refetchFunnel,
        error: funnelError,
    } = useFunnelAnalyticsQuery ? useFunnelAnalyticsQuery(
        {
            funnelId,
            storeIds: [storeId],
            startDate: queryRange.from,
            endDate: queryRange.to,
            environment,
        },
        {
            staleTime: 30000,
            enabled: hasSteps,
        }
    ) : { data: undefined, isLoading: false, refetch: () => { }, error: null };

    if (funnelError && process.env.NODE_ENV === 'development') {
        console.error('[FunnelAnalyticsPanel] Funnel analytics query error:', funnelError);
    }

    // Use backend-provided orderedStepIds for correct topological order
    // Falls back to safeSteps (prop order) if the backend hasn't returned data yet
    const orderedSteps = useMemo(() => {
        const backendOrder = (funnelAnalytics as any)?.orderedStepIds as string[] | undefined;
        if (backendOrder && backendOrder.length > 0) {
            return backendOrder
                .map(id => safeSteps.find(s => s.id === id))
                .filter((s): s is FunnelStep => s != null);
        }
        return safeSteps;
    }, [funnelAnalytics, safeSteps]);

    // When orderedSteps becomes available (after API loads), re-sync source/target.
    // Default the source to the first step WITH data — an untracked external
    // entry step would otherwise pin the progression chart to "No data".
    useEffect(() => {
        if (hasManuallySelectedRef.current || !orderedSteps.length) return;
        const firstOrdered = (orderedSteps.find(
            (s) => ((funnelAnalytics as any)?.steps?.[s.id]?.visitors ?? 0) > 0,
        ) ?? orderedSteps[0])?.id;
        if (!firstOrdered) return;
        if (firstOrdered !== sourceStepId) {
            setSourceStepId(firstOrdered);
        }
        const nextId = findNextStepId(firstOrdered, orderedSteps);
        if (nextId && nextId !== targetStepId) {
            setTargetStepId(nextId);
        }
    }, [orderedSteps]);

    // Auto-update when a step is selected externally (from canvas click).
    // Only react when the canvas selection actually CHANGES — this effect also
    // re-runs on sourceStepId changes, and re-asserting a stale canvas
    // selection there overwrites the user's manual dropdown pick (bounce bug).
    const lastAppliedCanvasStepRef = useRef<string | null>(null);
    useEffect(() => {
        const stepIdToUse = effectiveStepId;
        if (!stepIdToUse || !safeSteps.length) return;
        if (lastAppliedCanvasStepRef.current === stepIdToUse) return;

        const stepExists = safeSteps.some(s => s.id === stepIdToUse);
        if (!stepExists) return;

        lastAppliedCanvasStepRef.current = stepIdToUse;
        if (stepIdToUse !== sourceStepId) {
            hasManuallySelectedRef.current = true;
            setSourceStepId(stepIdToUse);
            const stepsRef = orderedSteps.length > 0 ? orderedSteps : safeSteps;
            const nextId = findNextStepId(stepIdToUse, stepsRef);
            if (nextId) {
                setTargetStepId(nextId);
            }
        }
    }, [effectiveStepId, safeSteps, sourceStepId, orderedSteps, findNextStepId]);

    // Calculate funnel-wide metrics using topological order
    const funnelMetrics = useMemo(() => {
        if (!funnelAnalytics?.steps) return null;

        const stepsArray = Object.values(funnelAnalytics.steps);
        const totalVisitors = stepsArray.reduce((sum, s) => sum + s.visitors, 0);
        const totalRealtime = stepsArray.reduce((sum, s) => sum + s.realtimeVisitors, 0);

        // Use topological order: first step = entry, last step = exit.
        // Entry anchors to the first ordered step that actually HAS visitors —
        // external/untracked entry steps (LP tracker never installed, or dead)
        // would otherwise zero out the whole headline while the rest of the
        // funnel clearly has traffic.
        const entryStep = orderedSteps.length > 0
            ? (orderedSteps
                .map((s) => funnelAnalytics.steps[s.id])
                .find((s) => s && s.visitors > 0)
                ?? funnelAnalytics.steps[orderedSteps[0].id])
            : stepsArray[0];
        const lastStep = orderedSteps.length > 0
            ? funnelAnalytics.steps[orderedSteps[orderedSteps.length - 1].id]
            : stepsArray[stepsArray.length - 1];

        // The topological tail can be reached by sessions that never passed the
        // anchor step (mid-funnel entries, reconverging branches), so the raw
        // ratio can exceed 1. Cap it: a funnel never converts more than everyone.
        const overallConversionRate = entryStep && lastStep && entryStep.visitors > 0
            ? Math.min(100, (lastStep.visitors / entryStep.visitors) * 100)
            : 0;

        return {
            totalVisitors,
            totalRealtime,
            entryVisitors: entryStep?.visitors || 0,
            exitVisitors: lastStep?.visitors || 0,
            overallConversionRate,
            stepsCount: orderedSteps.length || stepsArray.length,
        };
    }, [funnelAnalytics, orderedSteps]);

    // Biggest single step-to-step loss — the funnel's worst friction point. In a
    // 15-step upsell/downsell flow this is the one thing the operator must see first.
    const worstDrop = useMemo(() => {
        if (!funnelAnalytics?.steps || orderedSteps.length < 2) return null;
        let worst: { stepId: string; name: string; lost: number; pct: number } | null = null;
        for (let i = 1; i < orderedSteps.length; i++) {
            const cur = funnelAnalytics.steps[orderedSteps[i].id]?.visitors || 0;
            const prev = funnelAnalytics.steps[orderedSteps[i - 1].id]?.visitors || 0;
            const lost = prev - cur;
            if (lost > 0 && (!worst || lost > worst.lost)) {
                worst = { stepId: orderedSteps[i].id, name: orderedSteps[i].name, lost, pct: prev > 0 ? (lost / prev) * 100 : 0 };
            }
        }
        return worst;
    }, [funnelAnalytics, orderedSteps]);

    // Dense rendering once a funnel grows past a handful of steps (upsell stacks).
    const denseFunnel = orderedSteps.length > 7;

    // ── Branching flows (segmented bars + Sankey) ───────────────
    // Per-edge session counts from the backend, grouped by their source step.
    const flows = useMemo(
        () => ((funnelAnalytics as any)?.flows as FunnelFlow[] | undefined) ?? [],
        [funnelAnalytics],
    );
    const outgoingFlowsByStep = useMemo(() => {
        const map: Record<string, FunnelFlow[]> = {};
        for (const f of flows) {
            (map[f.source] ||= []).push(f);
        }
        return map;
    }, [flows]);
    // A step branches when it has 2+ outgoing edges that carried traffic.
    const branchingStepIds = useMemo(() => {
        const set = new Set<string>();
        for (const [source, outs] of Object.entries(outgoingFlowsByStep)) {
            if (outs.length >= 2 && outs.some(o => o.sessions > 0)) set.add(source);
        }
        return set;
    }, [outgoingFlowsByStep]);
    const hasBranching = branchingStepIds.size > 0;

    const stepNameById = useMemo(() => {
        const map: Record<string, string> = {};
        for (const s of safeSteps) map[s.id] = s.name;
        return map;
    }, [safeSteps]);
    const resolveStepName = useCallback((id: string) => stepNameById[id] || id, [stepNameById]);

    // Funnel overview rendering mode: classic step waterfall vs. flow (Sankey).
    const [funnelView, setFunnelView] = useState<'steps' | 'flow'>('steps');

    // ── Breakdown conversion scope ──────────────────────────────
    // The breakdown can measure conversion against the whole funnel (reached the
    // final step) or a single selected step (reached that step). It reacts to the
    // canvas node selection, falling back to the whole funnel when nothing is
    // selected; a header toggle lets the user override the current view.
    const selectedStepIndex = useMemo(
        () => (effectiveStepId ? orderedSteps.findIndex(s => s.id === effectiveStepId) : -1),
        [orderedSteps, effectiveStepId],
    );
    // Index 0 is the entry step (reached by 100% of sessions) — not a meaningful goal.
    const stepScopeAvailable = selectedStepIndex > 0;
    const stepScopeName = stepScopeAvailable ? orderedSteps[selectedStepIndex]?.name : undefined;

    const [breakdownScope, setBreakdownScope] = useState<BreakdownScope>('funnel');
    // Re-sync to the selection: scope a meaningful node, else the whole funnel.
    useEffect(() => {
        setBreakdownScope(stepScopeAvailable ? 'step' : 'funnel');
    }, [stepScopeAvailable, effectiveStepId]);

    // Conversion goal (name + baseline rate) for the current scope.
    const breakdownTarget = useMemo(() => {
        const fallbackName = orderedSteps.length > 0 ? orderedSteps[orderedSteps.length - 1]?.name : undefined;
        const funnelGoal = { rate: funnelMetrics?.overallConversionRate || 0, name: fallbackName };
        if (breakdownScope !== 'step' || !stepScopeAvailable || !funnelAnalytics?.steps || orderedSteps.length === 0) {
            return funnelGoal;
        }
        // Same entry anchor as the headline metric — orderedSteps[0] is often an
        // untracked external landing page, whose 0 visitors would zero every rate.
        const entryVisitors = funnelMetrics?.entryVisitors || 0;
        const stepObj = orderedSteps[selectedStepIndex];
        const stepVisitors = funnelAnalytics.steps[stepObj.id]?.visitors || 0;
        const rate = entryVisitors > 0 ? Math.min(100, (stepVisitors / entryVisitors) * 100) : 0;
        return { rate, name: stepObj?.name };
    }, [breakdownScope, stepScopeAvailable, selectedStepIndex, orderedSteps, funnelAnalytics, funnelMetrics]);

    const conversionStepName = breakdownTarget.name;

    // Breakdown goal step: the selected step when scoped to it, otherwise the whole
    // funnel (backend defaults to the funnel's last step when targetStepId is omitted).
    const breakdownTargetStepId = breakdownScope === 'step' && stepScopeAvailable
        ? effectiveStepId ?? undefined
        : undefined;

    const useBreakdownQuery = trpc.analytics?.funnelBreakdown?.useQuery;
    const {
        data: breakdownData,
        isLoading: isLoadingBreakdown,
        refetch: refetchBreakdown,
        error: breakdownError,
    } = useBreakdownQuery ? useBreakdownQuery(
        {
            funnelId,
            storeIds: [storeId],
            startDate: queryRange.from,
            endDate: queryRange.to,
            environment,
            targetStepId: breakdownTargetStepId,
        },
        {
            staleTime: 30000,
            enabled: hasSteps,
        }
    ) : { data: undefined, isLoading: false, refetch: () => { }, error: null };

    if (breakdownError && process.env.NODE_ENV === 'development') {
        console.error('[FunnelAnalyticsPanel] Breakdown query error:', breakdownError);
    }

    // Refresh all data
    const handleRefresh = useCallback(() => {
        refetchProgression();
        refetchFunnel();
        refetchBreakdown();
    }, [refetchProgression, refetchFunnel, refetchBreakdown]);

    // Check loading state
    const isLoading = isLoadingFunnel || isLoadingBreakdown;

    // Show empty state if no steps
    if (!hasSteps) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center bg-background">
                <AlertTriangle className="h-10 w-10 text-amber-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('analytics.noStepsAvailable')}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                    {t('analytics.addStepsToView')}
                </p>
                {onClose && (
                    <Button variant="outline" size="sm" onClick={onClose}>
                        {t('analytics.closePanel')}
                    </Button>
                )}
            </div>
        );
    }

    return (
        <AnalyticsErrorBoundary onClose={onClose}>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'analytics' | 'sessions')} className={cn('flex flex-col h-full overflow-hidden bg-background', className)}>
                {/* Unified Header Bar */}
                <div className="flex-shrink-0 flex items-center justify-between h-11 px-3 border-b bg-background">
                    {/* Left: Tabs */}
                    <TabsList className="h-8 p-1">
                        <TabsTrigger value="analytics" className="h-6 px-2.5 text-xs gap-1.5">
                            <BarChart3 className="h-3 w-3" />
                            {t('analytics.analytics')}
                        </TabsTrigger>
                        <TabsTrigger value="sessions" className="h-6 px-2.5 text-xs gap-1.5">
                            <ListTree className="h-3 w-3" />
                            {t('analytics.sessions')}
                        </TabsTrigger>
                    </TabsList>

                    {/* Right: Environment + date range + granularity (unified) + Actions */}
                    <div className="flex items-center gap-1.5">
                        <Select
                            value={environment}
                            onValueChange={(v) => {
                                envTouchedRef.current = true;
                                setEnvironment(v as 'staging' | 'production');
                            }}
                        >
                            <SelectPrimitive.Trigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 px-2.5 text-[11px] font-semibold"
                                    title={t('analytics.envSelectorTitle')}
                                >
                                    <span
                                        className={cn(
                                            'mr-1.5 h-1.5 w-1.5 rounded-full',
                                            environment === 'production' ? 'bg-green-500' : 'bg-amber-500',
                                        )}
                                    />
                                    <SelectValue />
                                    <ChevronDown className="ml-1 h-3 w-3 opacity-50" />
                                </Button>
                            </SelectPrimitive.Trigger>
                            <SelectContent>
                                <SelectItem value="production" className="text-xs">
                                    {t('analytics.envLive')}
                                </SelectItem>
                                <SelectItem value="staging" className="text-xs">
                                    {t('analytics.envEditor')}
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        <FunnelAnalyticsFilters
                            dateRange={dateRange}
                            onDateRangeChange={setDateRange}
                            granularity={granularity}
                            onGranularityChange={setGranularity}
                            showGranularity={activeTab === 'analytics'}
                        />

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleRefresh}
                            disabled={isLoading || isLoadingProgression}
                            title={t('analytics.refresh')}
                            aria-label={t('analytics.refresh')}
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        >
                            <RefreshCw className={cn('h-3 w-3', (isLoading || isLoadingProgression) && 'animate-spin')} />
                        </Button>

                        {onToggleFullscreen && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onToggleFullscreen}
                                title={isFullscreen ? t('analytics.exitFullscreen') : t('analytics.enterFullscreen')}
                                aria-label={isFullscreen ? t('analytics.exitFullscreen') : t('analytics.enterFullscreen')}
                                className="hidden h-7 w-7 text-muted-foreground hover:text-foreground md:inline-flex"
                            >
                                {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                            </Button>
                        )}

                        {onClose && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                title={t('analytics.closePanel')}
                                aria-label={t('analytics.closePanel')}
                                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            >
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Analytics Tab Content */}
                <TabsContent value="analytics" className="flex-1 overflow-y-auto m-0">
                    <div className={cn('space-y-0', isFullscreen && 'mx-auto w-full max-w-[1640px] px-2 py-2')}>
                        {/* ── Error banner (visible instead of silent empty state) ── */}
                        {(funnelError || breakdownError || progressionError) && (
                            <div className="flex items-center justify-between gap-3 border-b border-destructive/30 bg-destructive/5 px-4 py-2.5">
                                <div className="flex items-center gap-2 text-xs text-destructive">
                                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span>{t('analytics.loadError')}</span>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={handleRefresh}
                                    disabled={isLoading || isLoadingProgression}
                                >
                                    <RefreshCw className={cn('mr-1.5 h-3 w-3', (isLoading || isLoadingProgression) && 'animate-spin')} />
                                    {t('analytics.retry')}
                                </Button>
                            </div>
                        )}

                        {/* ── Compact Summary Strip ──────────────────────── */}
                        <div className="border-b bg-card px-4 py-3">
                            {isLoadingFunnel ? (
                                <div className="flex items-center gap-6">
                                    {[...Array(4)].map((_, i) => (
                                        <Skeleton key={i} className="h-8 w-24" />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex items-center gap-5">
                                    <MetricPill
                                        icon={<Users className="h-3.5 w-3.5" />}
                                        label={t('analytics.visitors')}
                                        value={funnelMetrics?.entryVisitors || 0}
                                    />
                                    {(funnelMetrics?.totalRealtime ?? 0) > 0 && (
                                        <MetricPill
                                            icon={<Activity className="h-3.5 w-3.5 animate-pulse" />}
                                            label={t('analytics.live')}
                                            value={funnelMetrics?.totalRealtime || 0}
                                            variant="live"
                                        />
                                    )}
                                    <MetricPill
                                        icon={<Target className="h-3.5 w-3.5" />}
                                        label={t('analytics.conversion')}
                                        value={`${(funnelMetrics?.overallConversionRate || 0).toFixed(1)}%`}
                                        variant={(funnelMetrics?.overallConversionRate ?? 0) >= 20 ? 'positive' : 'neutral'}
                                    />
                                    <MetricPill
                                        icon={<ArrowRight className="h-3.5 w-3.5" />}
                                        label={t('analytics.steps')}
                                        value={funnelMetrics?.stepsCount || 0}
                                    />
                                </div>
                            )}
                        </div>

                        {/* ── Two-column body in full-screen: flow story (left) + segments (right) ── */}
                        <div className={cn(isFullscreen && 'grid grid-cols-1 gap-x-8 lg:grid-cols-2')}>
                        {/* Left column — the flow: overview waterfall + step progression */}
                        <div className="min-w-0">
                        {/* ── Visual Funnel Waterfall ─────────────────────── */}
                        <div className="px-4 py-4">
                            <div className="mb-3 flex items-center justify-between gap-2">
                                <h3 className="text-sm font-semibold text-foreground">{t('analytics.funnelOverview')}</h3>
                                <div className="flex items-center gap-2">
                                    {funnelMetrics && funnelMetrics.entryVisitors > 0 && (
                                        <span className="text-[10px] font-medium text-muted-foreground">
                                            {t('analytics.enteredReachedEnd', { entered: funnelMetrics.entryVisitors.toLocaleString(), reached: funnelMetrics.exitVisitors.toLocaleString() })}
                                        </span>
                                    )}
                                    {flows.length > 0 && (
                                        <div className="flex items-center rounded-md border bg-muted/40 p-0.5">
                                            <button
                                                type="button"
                                                onClick={() => setFunnelView('steps')}
                                                title={t('analytics.viewSteps')}
                                                className={cn(
                                                    'flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                                                    funnelView === 'steps' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                                                )}
                                            >
                                                <AlignStartVertical className="h-3 w-3" />
                                                {t('analytics.viewSteps')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFunnelView('flow')}
                                                title={t('analytics.viewFlow')}
                                                className={cn(
                                                    'flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                                                    funnelView === 'flow' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                                                )}
                                            >
                                                <Workflow className="h-3 w-3" />
                                                {t('analytics.viewFlow')}
                                                {hasBranching && <span className="h-1 w-1 rounded-full bg-primary" />}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Biggest friction point — jump straight to the worst step (key for long funnels) */}
                            {!isLoadingFunnel && worstDrop && (funnelMetrics?.entryVisitors ?? 0) > 0 && (
                                <button
                                    type="button"
                                    onClick={() => onStepSelect?.(worstDrop.stepId)}
                                    className="mb-3 flex w-full items-center gap-2 rounded-md bg-rose-500/[0.08] px-2.5 py-1.5 text-left transition-colors hover:bg-rose-500/[0.12]"
                                >
                                    <TrendingDown className="h-3.5 w-3.5 flex-shrink-0 text-rose-500" />
                                    <span className="min-w-0 truncate text-[11px] text-foreground">
                                        {t('analytics.biggestDrop')} <span className="font-semibold">{worstDrop.name}</span>
                                    </span>
                                    <span className="ml-auto flex-shrink-0 text-[11px] font-bold tabular-nums text-rose-600 dark:text-rose-400">
                                        −{worstDrop.lost.toLocaleString()} ({worstDrop.pct.toFixed(0)}%)
                                    </span>
                                </button>
                            )}

                            {isLoadingFunnel ? (
                                <div className="space-y-1.5">
                                    {[...Array(5)].map((_, i) => (
                                        <Skeleton key={i} className="h-9 w-full rounded-md" />
                                    ))}
                                </div>
                            ) : funnelView === 'flow' ? (
                                <FunnelSankey
                                    steps={orderedSteps.map(s => ({ id: s.id, name: s.name }))}
                                    flows={flows}
                                    height={isFullscreen ? 560 : 420}
                                />
                            ) : funnelAnalytics?.steps ? (
                                <TooltipProvider delayDuration={150}>
                                    <div className={cn(denseFunnel ? 'space-y-0.5' : 'space-y-1')}>
                                        {orderedSteps.map((step, index) => {
                                            const analytics = funnelAnalytics.steps[step.id];
                                            const visitors = analytics?.visitors || 0;
                                            const entryVisitors = funnelMetrics?.entryVisitors || 1;
                                            const barWidth = entryVisitors > 0 ? Math.min(100, Math.max(2, (visitors / entryVisitors) * 100)) : 0;
                                            const isSplitTest = step.variants && step.variants.length > 0;
                                            const isSelected = selectedStepId === step.id;
                                            const isVariantSelected = step.variants?.some(v => selectedStepId === v.id);
                                            const isActive = isSelected || isVariantSelected;
                                            const isEndStep = !hasOutgoingEdges(step.id);
                                            const rate = analytics?.progressionRate;

                                            const prevStep = index > 0 ? orderedSteps[index - 1] : null;
                                            const prevAnalytics = prevStep ? funnelAnalytics.steps[prevStep.id] : null;
                                            const prevVisitors = prevAnalytics?.visitors || 0;
                                            const prevWidth = entryVisitors > 0 ? Math.min(100, (prevVisitors / entryVisitors) * 100) : 0;
                                            const dropOff = prevVisitors > 0 ? prevVisitors - visitors : 0;
                                            const dropOffPct = prevVisitors > 0 ? ((dropOff / prevVisitors) * 100) : 0;
                                            const isWorst = worstDrop?.stepId === step.id;

                                            // Bar colour reflects step-to-step retention, not absolute reach, so a
                                            // downsell that keeps 90% still reads as healthy late in a long flow.
                                            const fillColor = isEndStep
                                                ? 'bg-emerald-500'
                                                : rate == null
                                                    ? 'bg-blue-500'
                                                    : rate >= 70
                                                        ? 'bg-emerald-500'
                                                        : rate >= 40
                                                            ? 'bg-blue-500'
                                                            : rate >= 20
                                                                ? 'bg-amber-500'
                                                                : 'bg-rose-500';

                                            return (
                                                <div key={step.id}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <div
                                                                onClick={() => onStepSelect?.(isActive ? null : step.id)}
                                                                className={cn(
                                                                    'group relative flex cursor-pointer items-center gap-2.5 rounded-md px-2 transition-colors',
                                                                    denseFunnel ? 'py-1' : 'py-1.5',
                                                                    isActive ? 'bg-primary/5 ring-1 ring-primary/30' : 'hover:bg-muted/50',
                                                                )}
                                                            >
                                                                {/* Step number */}
                                                                <div className={cn(
                                                                    'flex flex-shrink-0 items-center justify-center rounded-full font-bold',
                                                                    denseFunnel ? 'h-4 w-4 text-[9px]' : 'h-5 w-5 text-[10px]',
                                                                    isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                                                                )}>
                                                                    {index + 1}
                                                                </div>

                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <div className="flex min-w-0 items-center gap-1.5">
                                                                            <span className="truncate text-xs font-medium">{step.name}</span>
                                                                            {isSplitTest && (
                                                                                <Badge variant="secondary" className="h-3.5 gap-0.5 border-purple-500/20 bg-purple-500/10 px-1 text-[8px] font-medium text-purple-600">
                                                                                    <Split className="h-2 w-2" />
                                                                                    A/B
                                                                                </Badge>
                                                                            )}
                                                                            {isWorst && !denseFunnel && (
                                                                                <span className="flex-shrink-0 rounded bg-rose-500/10 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">
                                                                                    {t('analytics.worstStepTag')}
                                                                                </span>
                                                                            )}
                                                                            {analytics?.realtimeVisitors && analytics.realtimeVisitors > 0 ? (
                                                                                <span className="flex items-center gap-0.5">
                                                                                    <span className="relative flex h-1.5 w-1.5">
                                                                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                                                                                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                                                                                    </span>
                                                                                    <span className="text-[9px] font-bold text-green-600">{analytics.realtimeVisitors}</span>
                                                                                </span>
                                                                            ) : null}
                                                                        </div>
                                                                        <div className="flex flex-shrink-0 items-center gap-1.5 tabular-nums">
                                                                            <span className="text-xs font-bold">{visitors.toLocaleString()}</span>
                                                                            {!isSplitTest && rate != null && !isEndStep && (
                                                                                <span className={cn(
                                                                                    'rounded px-1 py-0.5 text-[9px] font-semibold',
                                                                                    rate >= 50 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                                                                        : rate >= 20 ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                                                                                            : 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
                                                                                )}>
                                                                                    {rate.toFixed(0)}%
                                                                                </span>
                                                                            )}
                                                                            {isEndStep && <Badge variant="outline" className="h-4 border-green-300 px-1 text-[8px] text-green-600">{t('analytics.end')}</Badge>}
                                                                        </div>
                                                                    </div>

                                                                    {/* Funnel bar: faint backdrop = previous step's reach, solid =
                                                                        this step. The visible gap between them is the drop-off here. */}
                                                                    <div className={cn('relative mt-1 w-full overflow-hidden rounded-full bg-muted', denseFunnel ? 'h-1.5' : 'h-2')}>
                                                                        {index > 0 && prevWidth > barWidth && (
                                                                            <div className="absolute inset-y-0 left-0 rounded-full bg-foreground/10" style={{ width: `${prevWidth}%` }} />
                                                                        )}
                                                                        <div
                                                                            className={cn('absolute inset-y-0 left-0 rounded-full transition-[width] duration-500', fillColor)}
                                                                            style={{ width: `${barWidth}%` }}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {isSplitTest && (
                                                                    <ChevronDown className={cn(
                                                                        'h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform duration-200',
                                                                        isActive && 'rotate-180',
                                                                    )} />
                                                                )}
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="left" className="max-w-xs text-xs" sideOffset={8}>
                                                            <div className="space-y-1">
                                                                <p className="font-semibold">{step.name}</p>
                                                                <p>{t('analytics.visitorsOfEntry', { total: visitors.toLocaleString(), percent: barWidth.toFixed(0) })}</p>
                                                                {rate != null && !isEndStep && <p>{t('analytics.progression', { rate: rate.toFixed(1) })}</p>}
                                                                {analytics?.conversionValue != null && analytics.conversionValue > 0 && (
                                                                    <p>{t('analytics.revenue', { value: analytics.conversionValue.toLocaleString(), unit: analytics.conversionValueUnit })}</p>
                                                                )}
                                                                {dropOff > 0 && <p className="text-rose-400">{t('analytics.dropOffFromPrevious', { total: dropOff.toLocaleString(), percent: dropOffPct.toFixed(1) })}</p>}
                                                            </div>
                                                        </TooltipContent>
                                                    </Tooltip>

                                                    {/* Variant breakdown (expanded) */}
                                                    {isSplitTest && isActive && step.variants && (
                                                        <VariantBreakdown
                                                            variants={step.variants}
                                                            variantAnalytics={funnelAnalytics.variants}
                                                            stepVisitors={visitors}
                                                            selectedStepId={selectedStepId}
                                                            parentStepId={step.id}
                                                            onStepSelect={onStepSelect}
                                                        />
                                                    )}

                                                    {/* Branch breakdown (Accept/Decline/… offshoots of the spine) */}
                                                    {branchingStepIds.has(step.id) && (
                                                        <BranchBreakdown
                                                            visitors={visitors}
                                                            flows={outgoingFlowsByStep[step.id] || []}
                                                            stepName={resolveStepName}
                                                            dense={denseFunnel}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </TooltipProvider>
                            ) : (
                                <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
                                    {t('analytics.noStepData')}
                                </div>
                            )}
                        </div>

                        {/* ── Step Progression Chart ──────────────────────── */}
                        <div className="border-t px-4 py-4">
                            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                                <div className="min-w-0">
                                    <h3 className="text-sm font-semibold text-foreground">{t('analytics.stepProgressionOverTime')}</h3>
                                    <p className="text-[10px] text-muted-foreground">{t('analytics.stepProgressionHelp')}</p>
                                </div>
                                <div className="flex w-full items-center gap-1.5 sm:w-auto sm:flex-shrink-0">
                                    <Select value={sourceStepId} onValueChange={(v) => {
                                        hasManuallySelectedRef.current = true;
                                        // Picking the target's step swaps the pair instead of no-oping
                                        if (v === targetStepId) {
                                            setTargetStepId(sourceStepId);
                                        }
                                        setSourceStepId(v);
                                    }}>
                                        {/* Value span must be shrinkable (min-w-0) and wrappable for the
                                            base line-clamp-1 to ellipsize — the trigger's whitespace-nowrap
                                            otherwise lets long step names overflow the fixed-width box. */}
                                        <SelectTrigger className="h-7 min-w-0 flex-1 overflow-hidden text-[11px] sm:w-[170px] sm:flex-none *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:whitespace-normal">
                                            <SelectValue placeholder={t('analytics.fromStep')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {orderedSteps.map((step, idx) => (
                                                <SelectItem
                                                    key={step.id}
                                                    value={step.id}
                                                    className="text-xs"
                                                >
                                                    {idx + 1}. {step.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                    <Select value={targetStepId} onValueChange={(v) => {
                                        hasManuallySelectedRef.current = true;
                                        // Picking the source's step swaps the pair instead of no-oping
                                        if (v === sourceStepId) {
                                            setSourceStepId(targetStepId);
                                        }
                                        setTargetStepId(v);
                                    }}>
                                        <SelectTrigger className="h-7 min-w-0 flex-1 overflow-hidden text-[11px] sm:w-[170px] sm:flex-none *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:whitespace-normal">
                                            <SelectValue placeholder={t('analytics.toStep')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {orderedSteps.map((step, idx) => (
                                                <SelectItem
                                                    key={step.id}
                                                    value={step.id}
                                                    className="text-xs"
                                                >
                                                    {idx + 1}. {step.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {(() => {
                                const isSourceEndStep = !hasOutgoingEdges(sourceStepId);

                                if (isSourceEndStep) {
                                    return (
                                        <div className="flex h-[180px] flex-col items-center justify-center gap-1 text-muted-foreground">
                                            <span className="text-xl text-green-500">✓</span>
                                            <span className="text-xs font-medium text-foreground">{sourceStep?.name}</span>
                                            <span className="text-[11px]">{t('analytics.finalStepNoProgression')}</span>
                                        </div>
                                    );
                                }

                                if (sourceStepId === targetStepId) {
                                    return (
                                        <div className="flex h-[180px] items-center justify-center text-xs text-muted-foreground">
                                            {t('analytics.selectDifferentSteps')}
                                        </div>
                                    );
                                }

                                return (
                                    <StepProgressionChart
                                        data={progressionData ? {
                                            dataPoints: progressionData.dataPoints,
                                            summary: progressionData.summary,
                                            isSplitTest: progressionData.isSplitTest,
                                            variants: progressionData.variants,
                                            variantDataPoints: progressionData.variantDataPoints,
                                            variantSummaries: progressionData.variantSummaries,
                                        } : undefined}
                                        sourceStepName={sourceStep?.name || t('analytics.source')}
                                        targetStepName={targetStep?.name || t('analytics.target')}
                                        isLoading={isLoadingProgression}
                                        showVariantComparison={true}
                                    />
                                );
                            })()}
                        </div>

                        {/* end left column */}
                        </div>
                        {/* Right column — segments: conversion breakdown */}
                        <div className={cn('min-w-0', isFullscreen && 'lg:border-l lg:border-border')}>
                        {/* ── Conversion Breakdown by property ──────────── */}
                        <FunnelBreakdownPanel
                            data={breakdownData}
                            isLoading={isLoadingBreakdown}
                            overallConversionRate={breakdownTarget.rate}
                            conversionStepName={conversionStepName}
                            scope={breakdownScope}
                            onScopeChange={setBreakdownScope}
                            stepScopeAvailable={stepScopeAvailable}
                            stepScopeName={stepScopeName}
                            bordered={!isFullscreen}
                        />
                        {/* end right column */}
                        </div>
                        {/* end two-column body */}
                        </div>
                    </div>
                </TabsContent>

                {/* Sessions Tab Content */}
                <TabsContent value="sessions" className="flex-1 overflow-hidden m-0">
                    <SessionTracesPanel
                        funnelId={funnelId}
                        storeId={storeId}
                        steps={orderedSteps}
                        dateRange={dateRange}
                        environment={environment}
                        selectedStepId={effectiveStepId}
                        variantId={selectedVariantInfo?.variantId}
                        onStepSelect={onStepSelect}
                        className="h-full"
                    />
                </TabsContent>
            </Tabs>
        </AnalyticsErrorBoundary>
    );
});

// ── Compact Metric Pill ──────────────────────────────────
interface MetricPillProps {
    icon: React.ReactNode;
    label: string;
    value: number | string;
    variant?: 'neutral' | 'positive' | 'live';
}

function MetricPill({ icon, label, value, variant = 'neutral' }: MetricPillProps) {
    return (
        <div className="flex items-center gap-1.5">
            <span className={cn(
                "text-muted-foreground",
                variant === 'live' && "text-green-500",
                variant === 'positive' && "text-emerald-500",
            )}>
                {icon}
            </span>
            <div className="flex items-baseline gap-1">
                <span className={cn(
                    "text-base font-bold tabular-nums tracking-tight",
                    variant === 'live' && "text-green-600 dark:text-green-400",
                    variant === 'positive' && "text-emerald-600 dark:text-emerald-400",
                )}>
                    {typeof value === 'number' ? value.toLocaleString() : value}
                </span>
                <span className="text-[10px] font-medium text-muted-foreground">{label}</span>
            </div>
        </div>
    );
}

// ── Variant Breakdown Component ─────────────────────────
const VARIANT_COLORS = [
    { bg: 'bg-purple-500', text: 'text-purple-600 dark:text-purple-400', light: 'bg-purple-100 dark:bg-purple-900/30', ring: 'ring-purple-200', label: 'text-purple-700 dark:text-purple-300' },
    { bg: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400', light: 'bg-blue-100 dark:bg-blue-900/30', ring: 'ring-blue-200', label: 'text-blue-700 dark:text-blue-300' },
    { bg: 'bg-green-500', text: 'text-green-600 dark:text-green-400', light: 'bg-green-100 dark:bg-green-900/30', ring: 'ring-green-200', label: 'text-green-700 dark:text-green-300' },
    { bg: 'bg-orange-500', text: 'text-orange-600 dark:text-orange-400', light: 'bg-orange-100 dark:bg-orange-900/30', ring: 'ring-orange-200', label: 'text-orange-700 dark:text-orange-300' },
] as const;

interface VariantBreakdownProps {
    variants: FunnelStepVariant[];
    variantAnalytics: Record<string, any> | undefined;
    /** Total sessions on the parent step, variant-tagged or not. */
    stepVisitors: number;
    selectedStepId?: string | null;
    parentStepId: string;
    onStepSelect?: (stepId: string | null) => void;
}

function VariantBreakdown({ variants, variantAnalytics, stepVisitors, selectedStepId, parentStepId, onStepSelect }: VariantBreakdownProps) {
    const { t } = useTranslation('storefront');

    // Sessions on the step that carry no variant assignment. They are real
    // traffic and counted in the step total, but the A/B split can't claim
    // them — showing them keeps the variants from silently not adding up.
    const untracked = useMemo(() => {
        const tagged = variants.reduce((sum, v) => sum + (variantAnalytics?.[v.id]?.visitors || 0), 0);
        return Math.max(0, stepVisitors - tagged);
    }, [variants, variantAnalytics, stepVisitors]);

    const maxVisitors = useMemo(() => {
        let max = untracked;
        for (const v of variants) {
            const va = variantAnalytics?.[v.id];
            if (va?.visitors > max) max = va.visitors;
        }
        return max || 1;
    }, [variants, variantAnalytics, untracked]);

    return (
        <div className="ml-8 mt-1 space-y-0.5 border-l-2 border-purple-200 pl-3 dark:border-purple-800">
            {variants.map((variant, idx) => {
                const va = variantAnalytics?.[variant.id];
                const colors = VARIANT_COLORS[idx % VARIANT_COLORS.length];
                const isActive = selectedStepId === variant.id;
                const visitors = va?.visitors || 0;
                const rate = va?.progressionRate;
                const barPct = maxVisitors > 0 ? Math.max(3, (visitors / maxVisitors) * 100) : 0;

                return (
                    <div
                        key={variant.id}
                        onClick={(e) => {
                            e.stopPropagation();
                            onStepSelect?.(isActive ? parentStepId : variant.id);
                        }}
                        className={cn(
                            "flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-all",
                            isActive ? `${colors.light} ring-1 ${colors.ring}` : "hover:bg-muted/40"
                        )}
                    >
                        <div className={cn("h-2 w-2 flex-shrink-0 rounded-full", colors.bg)} />
                        <div className="flex-1 min-w-0">
                            <div className="mb-0.5 flex items-center justify-between">
                                <span className="text-[11px] font-medium truncate">{variant.label}</span>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-[11px] font-bold tabular-nums">{visitors.toLocaleString()}</span>
                                    {rate != null && (
                                        <span className={cn("text-[10px] font-bold tabular-nums", colors.text)}>
                                            {rate.toFixed(1)}%
                                        </span>
                                    )}
                                    {va?.realtimeVisitors > 0 && (
                                        <span className="flex items-center gap-0.5">
                                            <span className="relative flex h-1.5 w-1.5">
                                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                                            </span>
                                            <span className="text-[9px] font-bold text-green-600">{va.realtimeVisitors}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted">
                                <div
                                    className={cn("absolute inset-y-0 left-0 rounded-full transition-all duration-500", colors.bg)}
                                    style={{ width: `${barPct}%`, opacity: 0.7 }}
                                />
                            </div>
                        </div>
                    </div>
                );
            })}

            {untracked > 0 && (
                <div
                    className="flex items-center gap-2 rounded-md px-2 py-1.5"
                    title={t('analytics.untrackedVariantHelp')}
                >
                    <div className="h-2 w-2 flex-shrink-0 rounded-full bg-muted-foreground/40" />
                    <div className="min-w-0 flex-1">
                        <div className="mb-0.5 flex items-center justify-between">
                            <span className="truncate text-[11px] font-medium text-muted-foreground">
                                {t('analytics.untrackedVariant')}
                            </span>
                            <span className="flex-shrink-0 text-[11px] font-bold tabular-nums text-muted-foreground">
                                {untracked.toLocaleString()}
                            </span>
                        </div>
                        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                                className="absolute inset-y-0 left-0 rounded-full bg-muted-foreground/30"
                                style={{ width: `${Math.max(3, (untracked / maxVisitors) * 100)}%` }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

